import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useToggle, useClickOutside, usePrevious } from 'hooks';

export const MENU_ANCHOR_LEFT = 'left';
export const MENU_ANCHOR_RIGHT = 'right';
export const MENU_ANCHOR_BOTTOM_LEFT = 'bottomLeft';
export const MENU_ANCHOR_BOTTOM_RIGHT = 'bottomRight';
export const MENU_ANCHOR_OPTS = [
  MENU_ANCHOR_LEFT,
  MENU_ANCHOR_RIGHT,
  MENU_ANCHOR_BOTTOM_LEFT,
  MENU_ANCHOR_BOTTOM_RIGHT,
];

const DropdownMenu = ({
  containerRef,
  maxHeight,
  isOpen = false,
  anchor = MENU_ANCHOR_LEFT,
  usePortal = false,
  onClose = () => {},
  children,
}) => {
  const [containerRect, setContainerRect] = useState({});

  const prevVisible = usePrevious(isOpen);
  useEffect(() => {
    if (isOpen) {
      const { bottom, left, width } = containerRef.current.getBoundingClientRect();
      setContainerRect({ bottom, left, width });
    } else if (prevVisible) {
      onClose();
    }
  }, [isOpen]);

  const positionerStyle = {
    height: 1,
    left: containerRect.left,
    position: 'absolute',
    top: (containerRect.bottom || 0) + (window.pageYOffset || 0),
    width: containerRect.width,
  };

  if (containerRect.bottom && !maxHeight) {
    maxHeight = window.innerHeight - containerRect.bottom - 60
  }

  const menuStyle = {
    display: isOpen ? 'block' : 'none',
    position: 'absolute',
    maxHeight: maxHeight,
    overflow: 'auto',
    zIndex: 20,
  };
  switch (anchor) {
    case MENU_ANCHOR_LEFT:
      menuStyle.left = 0;
      menuStyle.top = 'calc(100% + 10px)';
      break;
    case MENU_ANCHOR_RIGHT:
      menuStyle.right = 0;
      menuStyle.top = 'calc(100% + 10px)';
      break;
    case MENU_ANCHOR_BOTTOM_LEFT:
      menuStyle.left = 0;
      menuStyle.bottom = 'calc(100% + 10px)';
      break;
    case MENU_ANCHOR_BOTTOM_RIGHT:
      menuStyle.right = 0;
      menuStyle.bottom = 'calc(100% + 10px)';
      break;
    default:
      break;
  }

  const menu = <div className="shadow bg-body rounded-3" style={menuStyle}>{children}</div>;

  return usePortal ? createPortal(<div style={positionerStyle}>{menu}</div>, document.body) : menu
};

DropdownMenu.propTypes = {
  containerRef: PropTypes.object,
  maxHeight: PropTypes.number,
  isOpen: PropTypes.bool,
  anchor: PropTypes.oneOf(MENU_ANCHOR_OPTS),
  usePortal: PropTypes.bool,
  closeOnChanged: PropTypes.any, // Menu will close whenever this value changes.
  closeTimeout: PropTypes.number,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default DropdownMenu;
